import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useCreateExternalLabelMutation,
  useSendExternalOrderEmailUpdatesMutation,
  useUpdateBulkExternalOrderStatusesMutation,
  useValidateExternalLabelMutation,
} from '../../features/ordersApiSlice';
import { Button, Card, CardBody, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Btn } from '../../AbstractElements';
import DataTable from 'react-data-table-component';
import { FaEdit } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { mergeAndDownloadShipmentLabels } from '../../utils';
import {
  DHL_ROW_STATUS,
  DHL_ROW_STATUS_CLASS,
  getDhlErrorText,
  getDhlRowStatus,
  getDhlStatusDetail,
  getDhlValidateResult,
  hasBlockingDhlError,
  cleanConsignee,
  getPackstationErrors,
  isDhlErrorMessage,
  isPackstation as isPackstationConsignee,
} from '../../utils/dhlValidation';
import PackstationBadges from '../Common/PackstationBadges';
import { RxCross2 } from 'react-icons/rx';

const PRODUCTS = [
  { label: 'DHL Paket', value: 'V01PAK', deBillingNo_dev: '33333333330102', deBillingNo: '63749964760101' },
  {
    label: 'DHL Kleinpaket',
    value: 'V62KP',
    deBillingNo_dev: '33333333336201',
    deBillingNo: '63749964766201',
  },
];

const ExternalOrdersShipmentLabels = () => {
  const [searchParams] = useSearchParams();
  const orderIds = searchParams.get('orderIds');
  const [orderValidations, setOrderValidations] = useState();
  const [dhlLabels, setDhlLabels] = useState();
  const [selectedOrder, setSelectedOrder] = useState();
  const [editModal, setEditModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusUpdated, setStatusUpdated] = useState(false);
  const deBillingNumberCode = process.env.REACT_APP_ENV === 'dev' ? 'deBillingNo_dev' : 'deBillingNo';
  const [createShipmentLabel, { isLoading }] = useCreateExternalLabelMutation();
  const [validateShipmentLabel, { isLoading: validateShipmentLabelLoading }] =
    useValidateExternalLabelMutation();
  const [bulkStatusUpdate, { isLoading: bulkStatusUpdateLoading }] =
    useUpdateBulkExternalOrderStatusesMutation();
  const [sendEmailUpdates, { isLoading: sendEmailUpdatesLoading }] =
    useSendExternalOrderEmailUpdatesMutation();

  // Only a genuine rejection blocks the batch. Shipments DHL accepted with notes
  // attached (warnings) stay visible in the table but keep the actions enabled.
  const hasValidationError = useMemo(() => hasBlockingDhlError(orderValidations), [orderValidations]);

  const validateShipmentLabels = async () => {
    try {
      const response = await createShipmentLabel({
        orderIds: JSON.parse(orderIds),
        validate: 'true',
      });
      console.log('response => ', response?.data?.data);
      setOrderValidations(response?.data?.data || response?.error?.data?.data);
    } catch (err) {
      console.log('Error => ', err);
    }
  };

  useEffect(() => {
    validateShipmentLabels();
  }, [orderIds]);

  const tableColumns = [
    {
      name: 'Order#',
      selector: (row) => `${row?.order?.internal_order_id || row?.order?.id}`,
      width: '80px',
    },
    {
      name: 'Name',
      selector: (row) => row?.customer?.full_name,
      // SHI-627 — Packstation rows carry the locker + Postnummer right under the name.
      cell: (row) => (
        <div className="t-py-1">
          <div>{row?.customer?.full_name}</div>
          <PackstationBadges consignee={row?.shipmentData?.consignee} />
        </div>
      ),
      width: '200px',
      center: false,
    },
    {
      name: 'Status',
      width: '200px',
      selector: (row) => {
        const rowStatus = getDhlRowStatus(row);
        return (
          <p className={DHL_ROW_STATUS_CLASS[rowStatus]} title={getDhlStatusDetail(row)}>
            {rowStatus}
          </p>
        );
      },
      center: false,
    },
    {
      // Warnings are listed alongside errors on purpose — DHL already accepted the
      // shipment, but the operator still needs to read what it changed and decide.
      name: 'Validation Messages',
      selector: (row) => (
        <ul className="t-list-disc">
          {row?.validations?.validationMessages?.map((item, i) => (
            <li
              key={'validation-' + i}
              className={isDhlErrorMessage(item) ? 't-text-red-500' : 't-text-amber-500'}
            >
              <span className="t-font-medium">
                {isDhlErrorMessage(item) ? 'Error' : 'Warning'}:
              </span>{' '}
              {item?.property ? `${item.property} — ` : ''}
              {item?.validationMessage}
            </li>
          ))}
        </ul>
      ),
      center: false,
      wrap: true,
    },
    {
      name: 'Action',
      width: '140px',
      cell: (row) => (
        <div className="t-flex t-gap-4">
          <FaEdit
            className="t-text-[#6610f2] t-text-lg"
            onClick={() => {
              setSelectedOrder(row);
              setEditModal(true);
            }}
          />
          <RxCross2
            className="t-text-red-500 t-text-lg t-font-bold"
            onClick={() => {
              const tempOrders = [...orderValidations];
              const updatedOrders = tempOrders.filter((item) => item?.order?.id !== row?.order?.id);
              setOrderValidations(updatedOrders);
            }}
          />
        </div>
      ),
    },
  ];

  const toggleModal = () => setEditModal(!editModal);

  const onInputChange = (name, value) => {
    setOrderValidations((prev) => {
      const index = prev.findIndex((item) => item?.order?.id === selectedOrder?.order?.id);
      if (index > -1) {
        const updatedOrders = [...prev];
        const updatedOrder = {
          ...updatedOrders[index],
          shipmentData: {
            ...updatedOrders[index].shipmentData,
            consignee: {
              ...updatedOrders[index].shipmentData.consignee,
              [name]: value,
            },
          },
        };
        updatedOrders[index] = updatedOrder;
        setSelectedOrder(updatedOrder);
        return updatedOrders;
      }

      return prev;
    });
  };

  const onDimensionChange = (name, value) => {
    setOrderValidations((prev) => {
      const index = prev.findIndex((item) => item?.order?.id === selectedOrder?.order?.id);
      if (index > -1) {
        const updatedOrders = [...prev];
        let updatedOrder;
        if (name === 'weight') {
          updatedOrder = {
            ...updatedOrders[index],
            shipmentData: {
              ...updatedOrders[index].shipmentData,
              details: {
                ...updatedOrders[index].shipmentData.details,
                weight: {
                  uom: 'kg',
                  value,
                },
              },
            },
          };
        } else {
          updatedOrder = {
            ...updatedOrders[index],
            shipmentData: {
              ...updatedOrders[index].shipmentData,
              details: {
                ...updatedOrders[index].shipmentData.details,
                dim: {
                  ...updatedOrders[index].shipmentData.details.dim,
                  [name]: value,
                },
              },
            },
          };
        }
        updatedOrders[index] = updatedOrder;
        setSelectedOrder(updatedOrder);
        return updatedOrders;
      }

      return prev;
    });
  };

  const onValidateShipment = async () => {
    try {
      // SHI-627 — catch a malformed locker / Postnummer before spending a DHL round-trip.
      const fieldErrors = getPackstationErrors(selectedOrder?.shipmentData?.consignee);
      if (fieldErrors.lockerID || fieldErrors.postNumber) {
        toast.error([fieldErrors.lockerID, fieldErrors.postNumber].filter(Boolean).join(' '));
        return;
      }
      const shipment = { ...selectedOrder?.shipmentData, consignee: cleanConsignee(selectedOrder?.shipmentData?.consignee) };
      const response = await validateShipmentLabel({
        shipments: [shipment],
        validate: 'true',
        partner: selectedOrder?.customer?.source_company_name,
      });
      console.log('Response => ', response);
      const result = getDhlValidateResult(response);
      // The server still reports a warning-only shipment as 'error' (and DHL answers it
      // with HTTP 400), so re-grade the payload here rather than trusting either.
      const rowStatus = result ? getDhlRowStatus(result) : DHL_ROW_STATUS.ERROR;

      if (result && rowStatus !== DHL_ROW_STATUS.ERROR) {
        setOrderValidations((prev) => {
          const index = prev.findIndex((item) => item?.order?.id === selectedOrder?.order?.id);

          if (index > -1) {
            const updatedOrders = [...prev];
            const updatedOrder = {
              ...updatedOrders[index],
              status: result?.status,
              validations: result?.validations,
            };
            updatedOrders[index] = updatedOrder;
            setSelectedOrder(updatedOrder);
            return updatedOrders;
          }

          return prev;
        });
        setEditModal(false);
        if (rowStatus === DHL_ROW_STATUS.WARNING) {
          toast.warning(
            `Order ${selectedOrder?.order?.id} accepted by DHL with warnings — review the messages before printing.`
          );
        } else {
          toast.success(`Order ${selectedOrder?.order?.id} label validation successfull!`);
        }
      } else {
        toast.error(getDhlErrorText(result, selectedOrder?.order?.id));
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  const createShipmentLabels = async () => {
    const batchInput = window.prompt('Enter batch number (e.g. 123):');
    if (!batchInput || !batchInput.trim()) return;
    const batchName = `PROD${batchInput.trim()}`;

    // Get company name — fall back to "External Orders" if mixed companies
    const companies = [...new Set(orderValidations?.map((o) => o?.customer?.source_company_name).filter(Boolean))];
    const companyLabel = companies.length === 1 ? companies[0] : 'External Orders';

    try {
      const loadingToast = toast.loading('Generating Labels...');
      const cleanedShipments = orderValidations
        ?.map((item) => item.shipmentData)
        ?.map((shipment) => ({ ...shipment, consignee: cleanConsignee(shipment.consignee) }));

      const response = await validateShipmentLabel({
        shipments: cleanedShipments,
        validate: 'false',
        partner: orderValidations?.[0]?.customer?.source_company_name,
      });
      if ([200, 207].includes(response?.data?.data?.status?.statusCode)) {
        setDhlLabels(response?.data?.data?.items);
        await mergeAndDownloadShipmentLabels(
          response?.data?.data?.items?.map((item) => item?.label?.url),
          `Shipping Labels ${companyLabel} orders - ${batchName}.pdf`
        );
        toast.dismiss(loadingToast);
        toast.success('Order labels created successfully!');
      } else {
        toast.dismiss(loadingToast);
        toast.error('Orders label creation failed. Please try again!');
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  console.log('dhlLabels => ', dhlLabels);
  const updateOrderStatuses = async () => {
    try {
      const loadingToast = toast.loading('Updating order statuses...');
      const orders = dhlLabels?.map((item) => ({
        orderId: item?.shipmentRefNo?.split('_')[1] + '_' + item?.shipmentRefNo?.split('_')[2],
        trackingId: item?.shipmentNo,
      }));
      await bulkStatusUpdate({
        orders,
      });
      setStatusUpdated(true);
      toast.dismiss(loadingToast);
      toast.success('Order statuses updated successfully!');
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  const onChangeProduct = (e) => {
    setOrderValidations((prev) => {
      const index = prev.findIndex((item) => item?.order?.id === selectedOrder?.order?.id);
      if (index > -1) {
        const updatedOrders = [...prev];
        const product = PRODUCTS?.find((item) => item.value === e.target.value);
        const updatedOrder = {
          ...updatedOrders[index],
          shipmentData: {
            ...updatedOrders[index].shipmentData,
            product: e.target.value,
            billingNumber: product[deBillingNumberCode],
          },
        };
        updatedOrders[index] = updatedOrder;
        setSelectedOrder(updatedOrder);
        return updatedOrders;
      }

      return prev;
    });
  };

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);

    if (state.selectedCount === 0) {
      setSelectedRows([]);
    }
  }, []);

  const sendEmailUpdatesSubmit = async () => {
    try {
      if (selectedRows?.length === 0) {
        toast.error('Please select orders to send emails!');
        return;
      }
      const loadingToast = toast.loading('Sending email updates...');
      const orderIds = selectedRows?.map((item) => item?.order?.id);
      console.log('orderIds => ', orderIds);
      const res = await sendEmailUpdates({ orderIds });
      toast.dismiss(loadingToast);
      if (res?.error?.data) {
        toast.error(res?.error?.data?.message || 'Something went wrong!');
      } else {
        toast.success('Order updates sent successfully!');
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  // Packstation vs normal is discriminated by the consignee shape the server built (Packstation
  // carries lockerID/postNumber; normal carries addressStreet). The operator can override via the
  // Address-type switch below.
  const isPackstation = isPackstationConsignee(selectedOrder?.shipmentData?.consignee);
  const packstationErrors = getPackstationErrors(selectedOrder?.shipmentData?.consignee);

  // Reshape the consignee between a normal street address and a DHL Packstation, keeping shared
  // fields. Persisted into the order (not just local state) so batch label creation uses it.
  const setDeliveryType = (packstation) => {
    setSelectedOrder((prev) => {
      if (!prev?.shipmentData?.consignee) return prev;
      const c = prev.shipmentData.consignee;
      let consignee;
      if (packstation) {
        const { name1, addressStreet, ...rest } = c;
        consignee = { ...rest, name: c.name || name1 || '', lockerID: c.lockerID || '', postNumber: c.postNumber || '' };
      } else {
        const { name, lockerID, postNumber, ...rest } = c;
        consignee = { ...rest, name1: c.name1 || name || '', addressStreet: c.addressStreet || '' };
      }
      const updated = { ...prev, shipmentData: { ...prev.shipmentData, consignee } };
      setOrderValidations((list) => list.map((it) => (it?.order?.id === prev?.order?.id ? updated : it)));
      return updated;
    });
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <h2>Orders Validations</h2>
      <Card>
        <CardBody>
          <div className="text-end t-flex t-gap-2 t-justify-end">
            <Btn
              attrBtn={{
                className: 'mr-3',
                color: 'primary',
                disabled:
                  hasValidationError ||
                  validateShipmentLabelLoading ||
                  bulkStatusUpdateLoading,
                onClick: createShipmentLabels,
              }}
            >
              Create Labels
            </Btn>
            <Btn
              attrBtn={{
                className: 'mr-2',
                color: 'secondary',
                disabled:
                  hasValidationError ||
                  validateShipmentLabelLoading ||
                  !dhlLabels ||
                  bulkStatusUpdateLoading,
                onClick: updateOrderStatuses,
              }}
            >
              Update Orders Status
            </Btn>
            <Btn
              attrBtn={{
                className: 'mr-2',
                color: 'primary',
                disabled:
                  hasValidationError ||
                  validateShipmentLabelLoading ||
                  !dhlLabels ||
                  bulkStatusUpdateLoading ||
                  sendEmailUpdatesLoading ||
                  !statusUpdated,
                onClick: sendEmailUpdatesSubmit,
              }}
            >
              Send Email Updates
            </Btn>
          </div>

          <DataTable
            columns={tableColumns}
            data={orderValidations}
            striped={true}
            center={true}
            pagination
            paginationServer
            progressPending={isLoading}
            selectableRows
            selectableRowsHighlight
            onSelectedRowsChange={handleRowSelected}
          />
        </CardBody>
      </Card>
      <Modal isOpen={editModal} toggle={toggleModal} size="md">
        <ModalHeader className="t-text-xl t-font-bold" toggle={toggleModal}>
          Edit validation Data
        </ModalHeader>

        <ModalBody>
          <h1 className="t-text-lg t-font-bold">Data from order</h1>
          <DataRow heading="Order Id" desc={selectedOrder?.order?.internal_order_id} />
          <DataRow heading="Name" desc={selectedOrder?.customer?.full_name} />
          <DataRow heading="Addres" desc={selectedOrder?.customer?.address} />
          <DataRow heading="Postal Code" desc={selectedOrder?.customer?.zip_code} />
          <DataRow heading="City" desc={selectedOrder?.customer?.city} />
          <DataRow heading="Country" desc={selectedOrder?.customer?.country} />
          <DataRow heading="Phone number" desc={selectedOrder?.customer?.phone_number} />
          <div className="t-w-full t-flex t-flex-col t-gap-4 t-mt-8">
            <div className="t-flex t-items-center t-justify-between t-flex-wrap t-gap-2">
              <h1 className="t-text-lg t-font-bold">Data for DHL</h1>
              <div className="t-flex t-items-center t-gap-2">
                <span className="t-text-sm t-font-semibold">Address type:</span>
                {[{ v: false, l: 'Normal' }, { v: true, l: 'Packstation' }].map((o) => (
                  <button
                    key={o.l}
                    type="button"
                    onClick={() => setDeliveryType(o.v)}
                    style={{
                      background: !!isPackstation === o.v ? '#003b4d' : '#f3f4f6',
                      color: !!isPackstation === o.v ? '#fff' : '#4b5563',
                      border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="t-w-full t-flex t-items-center t-gap-4">
              {isPackstation ? (
                <Input
                  label="Name"
                  placeholder="Enter consignee name"
                  value={selectedOrder?.shipmentData?.consignee?.name}
                  onChange={(e) => onInputChange('name', e.target.value)}
                />
              ) : (
                <Input
                  label="Name"
                  placeholder="Enter consignee name"
                  value={selectedOrder?.shipmentData?.consignee?.name1}
                  onChange={(e) => onInputChange('name1', e.target.value)}
                />
              )}

              <Input
                label="Contact name"
                placeholder="Enter consignee contact name"
                value={selectedOrder?.shipmentData?.consignee?.contactName}
                onChange={(e) => onInputChange('contactName', e.target.value)}
              />
            </div>
            {/* DHL's locker schema has no name2 — only street addresses get it. */}
            {!isPackstation && (
              <Input
                label="Address Line 2 (name2)"
                placeholder="Enter consignee address line 2 (name2)"
                value={selectedOrder?.shipmentData?.consignee?.name2}
                onChange={(e) => onInputChange('name2', e.target.value)}
              />
            )}
            <div className="t-w-full t-flex t-items-center t-gap-4">
              <Input
                label="Email"
                placeholder="Enter consignee email"
                value={selectedOrder?.shipmentData?.consignee?.email}
                onChange={(e) => onInputChange('email', e.target.value)}
              />
              <Input
                label="Postal Code"
                placeholder="Enter consignee postal code"
                value={selectedOrder?.shipmentData?.consignee?.postalCode}
                onChange={(e) => onInputChange('postalCode', e.target.value)}
              />
            </div>
            <div className="t-w-full t-flex t-items-center t-gap-4">
              <Input
                label="City"
                placeholder="Enter consignee city"
                value={selectedOrder?.shipmentData?.consignee?.city}
                onChange={(e) => onInputChange('city', e.target.value)}
              />
              <Input
                label="Country"
                placeholder="Enter consignee country"
                value={selectedOrder?.shipmentData?.consignee?.country}
                onChange={(e) => onInputChange('country', e.target.value)}
              />
            </div>
            {isPackstation ? (
              <div className="t-w-full t-flex t-items-start t-gap-4">
                <>
                  <Input
                    label="Locker number (Packstation)"
                    placeholder="e.g. 123"
                    inputMode="numeric"
                    maxLength={3}
                    value={selectedOrder?.shipmentData?.consignee?.lockerID ?? ''}
                    onChange={(e) => onInputChange('lockerID', e.target.value.replace(/\D/g, ''))}
                    error={packstationErrors.lockerID}
                  />
                  <Input
                    label="Postnummer"
                    placeholder="6–10 digits"
                    inputMode="numeric"
                    value={selectedOrder?.shipmentData?.consignee?.postNumber ?? ''}
                    onChange={(e) => onInputChange('postNumber', e.target.value)}
                    error={packstationErrors.postNumber}
                  />
                </>
              </div>
            ) : (
              <>
                <Input
                  label="Address"
                  placeholder="Enter consignee address"
                  value={selectedOrder?.shipmentData?.consignee?.addressStreet}
                  onChange={(e) => onInputChange('addressStreet', e.target.value)}
                />
              </>
            )}

            {selectedOrder?.shipmentData?.consignee?.phone && (
              <div className="t-w-full t-flex t-items-center t-gap-4">
                <Input
                  label="Phone number"
                  placeholder="Enter consignee phone number"
                  value={selectedOrder?.shipmentData?.consignee?.phone}
                  onChange={(e) => onInputChange('phone', e.target.value)}
                />
              </div>
            )}
            <div className="t-w-full t-flex t-items-center t-gap-4">
              <Input
                label="Height (cm)"
                placeholder="Height"
                value={selectedOrder?.shipmentData?.details?.dim?.height}
                onChange={(e) => onDimensionChange('height', e.target.value)}
              />
              <Input
                label="Length (cm)"
                placeholder="Length"
                value={selectedOrder?.shipmentData?.details?.dim?.length}
                onChange={(e) => onDimensionChange('length', e.target.value)}
              />
              <Input
                label="Width (cm)"
                placeholder="Width"
                value={selectedOrder?.shipmentData?.details?.dim?.width}
                onChange={(e) => onDimensionChange('width', e.target.value)}
              />
              <Input
                label="Weight (kg)"
                placeholder="Weight"
                value={selectedOrder?.shipmentData?.details?.weight?.value}
                onChange={(e) => onDimensionChange('weight', e.target.value)}
              />
            </div>
            {selectedOrder?.shipmentData?.consignee?.country === 'DEU' && (
              <div className="t-w-full t-flex t-flex-col">
                <label className="t-font-medium">Product</label>
                <select
                  className="t-w-full t-p-2 t-border t-border-gray-200 t-rounded-md"
                  value={selectedOrder?.shipmentData?.product}
                  defaultValuealue={selectedOrder?.shipmentData?.product}
                  onChange={(e) => onChangeProduct(e)}
                >
                  {PRODUCTS.map((item) => (
                    <option value={item.value} key={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal} disabled={validateShipmentLabelLoading}>
            Cancel
          </Button>
          <Button color="primary" onClick={onValidateShipment} disabled={validateShipmentLabelLoading}>
            Validate
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const DataRow = ({ heading, desc }) => (
  <div className="t-flex t-items-center t-gap-2">
    <p className="t-font-medium">{heading}:</p>
    <p>{desc}</p>
  </div>
);

const Input = ({ label, placeholder, value, onChange, inputMode, maxLength, error }) => (
  <div className="t-w-full t-flex t-flex-col">
    <label className="t-font-medium">{label}</label>
    <input
      type="text"
      value={value}
      className={`t-w-full t-p-2 t-border t-rounded-md ${error ? 't-border-red-400' : 't-border-gray-200'}`}
      placeholder={placeholder}
      onChange={onChange}
      inputMode={inputMode}
      maxLength={maxLength}
    />
    {error && <p className="t-text-xs t-text-red-500 t-mt-1">{error}</p>}
  </div>
);

export default ExternalOrdersShipmentLabels;
